<template>
  <div class="c-table">
    <template v-if="tableData && tableData[0]">
      <slot name="action" />
      <div class="c-table__head">
        <template v-for="(key, i) in getKeys(tableData[0].rowData)">
          <div
            v-if="key !== 'permissions'"
            :key="i"
            :class="['c-table__cell', sortColumnDirection && sortColumnActive === key ? `c-table__cell-${sortColumnDirection}` : '']"
            @click="sortable && handleSort(key)"
          >
            <span>{{ getColumnLabel(key) }}</span>
          </div>
        </template>
        <div
          class="c-table__cell c-table__cell--actions"
        >
          {{ actionLabel }}
        </div>
      </div>
      <div class="c-table__body">
        <draggable v-model="tableData" :disabled="!draggable" @start="drag=true" @end="drag=false" @sort="sortHandler">
          <div v-for="(data, index) in preparedTableData" :key="useIdAsKey ? data.id : index" :class="['c-table__row', { 'c-table__row--draggable': draggable,'c-table__row--expanded': shouldRenderExpandedRow(tableData[index])}]">
            <template v-for="(key, i) in getKeys(data.rowData)">
              <div v-if="key !== 'permissions'" :key="i" class="c-table__cell">
                <span v-if="i === 0 && draggable" class="u-mr-xs"><md-icon>drag_indicator</md-icon></span>
                <span
                  v-if="isHistoryLog"
                  :class="data.rowData[key] ? data.rowData[key].className : ''"
                >{{ data.rowData[key] ? data.rowData[key].content : '' }}</span>
                <span
                  v-else-if="isBooleanValue(data.rowData[key])"
                  :class="['c-table__bool', { 'is-true': isTrue(data.rowData[key]) }]"
                >
                  <app-icon
                    :name="isTrue(data.rowData[key]) ? 'check' : 'remove'"
                    :size="14"
                  />
                  {{ isTrue(data.rowData[key]) ? 'Yes' : 'No' }}
                </span>
                <span v-else>{{ data.rowData[key] }}</span>
              </div>
            </template>
            <div class="c-table__cell c-table__cell--actions">
              <slot name="default" :selected="tableData[index]" :index="index" />
            </div>
            <div v-if="shouldRenderExpandedRow(tableData[index])" class="c-table__expanded-row">
              <slot name="expanded-row" :selected="tableData[index]" :index="index" />
            </div>
          </div>
        </draggable>
      </div>
    </template>
    <template v-else>
      <div v-if="!isTicketBanner" class="c-table__head" />
      <slot v-if="isTicketBanner" name="action" />
      <div class="c-table__body">
        <div class="c-table__empty">
          <span class="c-table__empty-icon">
            <app-icon
              name="inbox"
              :size="24"
            />
          </span>
          <p class="c-table__empty-title">
            {{ emptyTitle }}
          </p>
          <p>{{ emptyDescription }}</p>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { keys } from 'lodash';
import draggable from 'vuedraggable'
import AppIcon from '../app-icon/AppIcon';

// words that should read as acronyms in column headers
const COLUMN_ACRONYMS = ['id', 'url', 'iso', 'seo', 'api', 'cf', 'html', 'css', 'js', 'ip'];

export default {
  name: 'BaseTableComponent',

  components: {
    draggable,
    AppIcon,
  },

  props: {
    propData: {
      type: Array,
      required: true,
    },
    isTicketBanner: {
      type: Boolean,
      default: false
    },
    sortable: {
      type: Boolean,
      required: false,
      default: false,
    },
    actionLabel: {
      type: String,
      required: false,
      default: ''
    },
    draggable: {
      type: Boolean,
      required: false,
      default: false,
    },
    useIdAsKey: {
      type: Boolean,
      required: false,
      default: false,
    },
    expandedRowPredicate: {
      type: Function,
      required: false,
      default: () => false
    },
    expandedRowColspan: {
      type: Number,
      required: false,
      default: 1
    },
    isHistoryLog: {
      type: Boolean,
      required: false,
      default: false,
    },
    emptyTitle: {
      type: String,
      required: false,
      default: 'No results',
    },
    emptyDescription: {
      type: String,
      required: false,
      default: 'There is nothing to show here yet.',
    },
  },

  data() {
    return {
      tableData: [],
      sortColumnActive: '',
      sortColumnDirection: '',
      sortingDirections: ['asc', 'desc', ''],
    }
  },

  computed: {
    preparedTableData() {
      if (!this.isHistoryLog) {
        return this.tableData;
      }

      return this.tableData.map(row => {
        const currentRowData = row && row.rowData ? row.rowData : {};
        const parsedRowData = {};

        keys(currentRowData).forEach(key => {
          parsedRowData[key] = this.parseHistoryLogCell(currentRowData[key]);
        });

        return {
          ...row,
          rowData: parsedRowData,
        };
      });
    },
  },

  watch: {
    propData: {
      handler(newValue) {
        this.tableData = newValue;
      },
      deep: true
    }
  },

  created() {
    this.tableData = this.propData;
  },

  methods: {
    // booleans (and "true"/"false" strings) render as Yes/No badges
    isBooleanValue(value) {
      return typeof value === 'boolean' || value === 'true' || value === 'false';
    },

    isTrue(value) {
      return value === true || value === 'true';
    },

    // "betshopId" -> "Betshop ID", "countryIsoCode" -> "Country ISO Code"
    getColumnLabel(key) {
      if (key === 'phrase') {
        return 'Key';
      }

      return String(key)
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .split(' ')
        .filter(Boolean)
        .map(word => COLUMN_ACRONYMS.includes(word.toLowerCase())
          ? word.toUpperCase()
          : word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },

    parseHistoryLogCell(value) {
      if (value === null || value === undefined) {
        return {
          className: '',
          content: '',
        };
      }

      const normalizedValue = String(value);
      const match = normalizedValue.match(/<span\s+class=["']([^"']+)["']\s*><\/span>/i);
      const content = normalizedValue
        .replace(/<span\s+class=["'][^"']+["']\s*><\/span>/i, '')
        .replace(/<[^>]*>/g, '')
        .trim();

      return {
        className: match ? match[1] : '',
        content,
      };
    },

    sortHandler() {
      this.$emit('dragSort', this.tableData)
    },

    getKeys(data) {
      return keys(data);
    },

    shouldRenderExpandedRow(data) {
      const expandedRowSlotExists = Boolean(
        this.$scopedSlots['expanded-row'] ||
        this.$slots['expanded-row']
      );

      if (!expandedRowSlotExists) {
        return false;
      }

      return this.expandedRowPredicate(data);
    },

    handleSort(key) {
      let directionIndex = 0;

      if (this.sortColumnActive === key) {
        directionIndex = this.sortingDirections.findIndex(direction => {
          return direction === this.sortColumnDirection
        });

        directionIndex++;

        if (directionIndex >= this.sortingDirections.length) {
          directionIndex = 0;
        }
      }

      this.sortColumnActive = key;
      this.sortColumnDirection = this.sortingDirections[directionIndex]

      this.$emit('sort', key, this.sortColumnDirection)
    }
  },
};
</script>
